import mustache from "mustache";
import { procedimientoVeterinarioGrpcCatalogService } from "../../../../../modulos/catalogos/pesaje/ganaderia/services/ProcedimientoVeterinarioGrpcCatalogService";
import { ProcedimientoVeterinarioItem } from "../../../../../generated/grpc/catalogos_pesaje_pb";
import { RotacionPesajeVeterinariaRequest } from "../../../../../modulos/pesaje/ganaderia/request/RotacionPesajeVeterinariaRequest";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { toISODate } from "../../../../../utils/fechaUtils";
import { _2d, _miles } from "../../../../../utils/numberUtils";
import { IFormBodyRender } from "../../../../definitions/form/IFormBodyRender";
import { IFormField } from "../../../../definitions/form/IFormField";
import { InputDateField } from "../../../../implementations/form/date/InputDateField";
import { InputSwitchField } from "../../../../implementations/form/switch/InputSwitchField";
import { SummaryItemRender } from "../../../../implementations/summary/SummaryItemRender";
import { LoteSelectorField } from "../../../terceros/lote/form/LoteSelectorField";
import { createLoteInfoRender } from "../../../terceros/lote/loteInfo/createLoteInfoRender";
import footerTemplate from "./rotacionPesajeVeterinariaFooterTemplate.html";
import template from "./rotacionPesajeVeterinariaFormTemplate.html";

mustache.parse(template);
mustache.parse(footerTemplate);

/** Contador para ids únicos por instancia. */
let ROTACION_PESAJE_VETERINARIA_FORM_ID = 0;

/** Formulario de evento de Pesaje / Rotación / Veterinaria (Render/Connect).
 *  Card "Datos básicos" (potrero actual + fecha) y, en secciones activables por
 *  switch, las acciones del evento: rotación (destino + cantidad), pesaje y
 *  veterinaria. Expone `validate()` para que el modal contenedor decida si
 *  guardar. */
export class RotacionPesajeVeterinariaFormRender implements IFormBodyRender {
  /** Id único de la instancia (para los ids del DOM manuales). */
  private readonly _formId: number;
  private readonly _request: RotacionPesajeVeterinariaRequest;

  // === Card "Datos básicos" ===
  /** Selector del potrero origen (null cuando el origen viene fijo del mapa). */
  private readonly _loteOrigen: LoteSelectorField<RotacionPesajeVeterinariaRequest> | null;
  private readonly _fecha: InputDateField<RotacionPesajeVeterinariaRequest>;

  // === Card "Rotación de potrero" ===
  private readonly _switchRotacion: InputSwitchField<RotacionPesajeVeterinariaRequest>;
  private readonly _loteDestino: LoteSelectorField<RotacionPesajeVeterinariaRequest>;

  // === Card "Pesaje" ===
  private readonly _switchPesaje: InputSwitchField<RotacionPesajeVeterinariaRequest>;
  private readonly _pesoMin: SummaryItemRender;
  private readonly _pesoProm: SummaryItemRender;
  private readonly _pesoMax: SummaryItemRender;
  private readonly _pesoTotal: SummaryItemRender;

  // === Card "Tratamiento veterinario" ===
  private readonly _switchVeterinaria: InputSwitchField<RotacionPesajeVeterinariaRequest>;

  /** Campos con validación estándar (siempre activos). */
  private readonly _fields: IFormField[];
  /** Summaries de solo lectura (connect/dispose). */
  private readonly _summaries: SummaryItemRender[];

  /** Potrero origen fijo (viene del mapa): sin selector, solo su ficha. */
  private readonly _loteOrigenFijo?: ClienteFincaLoteResponse;

  /** Potrero origen actual (fijo o elegido); alimenta rotación y pesaje. */
  private _loteOrigenSelected: ClienteFincaLoteResponse | null;

  /** Al editar: potrero origen original del evento (el conteo actual del lote ya
   *  descontó los animales que este evento rotó). */
  private readonly _loteOrigenOriginalId: string;
  /** Al editar: cantidad de animales del evento original. */
  private readonly _cantidadOriginalEvento: number;
  /** Al editar: el evento original incluía rotación. */
  private readonly _rotacionOriginal: boolean;

  /**
   * @param request        Request a crear/editar (lo mutan los campos).
   * @param loteOrigenFijo Potrero origen fijo (viene del mapa), o undefined.
   */
  constructor(
    request: RotacionPesajeVeterinariaRequest,
    loteOrigenFijo?: ClienteFincaLoteResponse,
  ) {
    ROTACION_PESAJE_VETERINARIA_FORM_ID += 1;
    this._formId = ROTACION_PESAJE_VETERINARIA_FORM_ID;
    this._request = request;
    this._loteOrigenFijo = loteOrigenFijo;
    this._loteOrigenSelected = loteOrigenFijo ?? null;
    // En edición, los animales que este evento rotó cuentan como disponibles en
    // su potrero origen original.
    this._loteOrigenOriginalId = request.id ? request.loteOrigenId : "";
    this._cantidadOriginalEvento = request.id ? request.cantidadAnimales : 0;
    this._rotacionOriginal = request.id ? request.isRotacion : false;
    this._loteOrigen = loteOrigenFijo ? null : this._createLoteOrigen();
    this._fecha = this._createFecha();
    this._switchRotacion = this._createSwitchRotacion();
    this._loteDestino = this._createLoteDestino();
    this._switchPesaje = this._createSwitchPesaje();
    this._pesoMin = new SummaryItemRender({
      label: "Peso mínimo",
      description: "",
    });
    this._pesoProm = new SummaryItemRender({
      label: "Peso promedio",
      description: "",
    });
    this._pesoMax = new SummaryItemRender({
      label: "Peso máximo",
      description: "",
    });
    this._pesoTotal = new SummaryItemRender({
      label: "Total kg",
      description: "",
    });
    this._switchVeterinaria = this._createSwitchVeterinaria();
    this._fields = this._loteOrigen
      ? [
          this._loteOrigen,
          this._fecha,
          this._switchRotacion,
          this._switchPesaje,
          this._switchVeterinaria,
        ]
      : [
          this._fecha,
          this._switchRotacion,
          this._switchPesaje,
          this._switchVeterinaria,
        ];
    this._summaries = [
      this._pesoMin,
      this._pesoProm,
      this._pesoMax,
      this._pesoTotal,
    ];
  }

  render(): string {
    return mustache.render(template, {
      id: this._formId,
      // Origen fijo: solo la ficha del potrero; si no, el selector completo.
      loteOrigen: this._loteOrigenFijo
        ? createLoteInfoRender(this._loteOrigenFijo).render()
        : this._loteOrigen!.render(),
      fecha: this._fecha.render(),
      switchRotacion: this._switchRotacion.render(),
      loteDestino: this._loteDestino.render(),
      switchPesaje: this._switchPesaje.render(),
      pesoMin: this._pesoMin.render(),
      pesoProm: this._pesoProm.render(),
      pesoMax: this._pesoMax.render(),
      pesoTotal: this._pesoTotal.render(),
      switchVeterinaria: this._switchVeterinaria.render(),
    });
  }

  /** Tira "Se registrará:" del footer del modal: un badge por sección activa y
   *  el error de "ninguna sección activa". La monta el modal (footer left). */
  renderFooterBadges(): string {
    return mustache.render(footerTemplate, { id: this._formId });
  }

  connect(): void {
    this._fields.forEach((field) => field.connect());
    this._loteDestino.connect();
    this._summaries.forEach((summary) => summary.connect());

    this._rotacionBody = document.getElementById(
      `rpv-rotacion-body-${this._formId}`,
    );
    this._cantidadInput = document.getElementById(
      `rpv-cantidad-${this._formId}`,
    ) as HTMLInputElement | null;
    this._cantidadError = document.getElementById(
      `rpv-cantidad-error-${this._formId}`,
    );
    this._pesajeBody = document.getElementById(
      `rpv-pesaje-body-${this._formId}`,
    );
    this._pesosContainer = document.getElementById(`rpv-pesos-${this._formId}`);
    this._pesosError = document.getElementById(
      `rpv-pesos-error-${this._formId}`,
    );
    this._veterinariaBody = document.getElementById(
      `rpv-veterinaria-body-${this._formId}`,
    );
    this._procedimientosContainer = document.getElementById(
      `rpv-procedimientos-${this._formId}`,
    );
    this._procedimientosError = document.getElementById(
      `rpv-procedimientos-error-${this._formId}`,
    );
    this._observacionesInput = document.getElementById(
      `rpv-observaciones-${this._formId}`,
    ) as HTMLTextAreaElement | null;
    this._badgeRotacion = document.getElementById(
      `rpv-badge-rotacion-${this._formId}`,
    );
    this._badgePesaje = document.getElementById(
      `rpv-badge-pesaje-${this._formId}`,
    );
    this._badgeVeterinaria = document.getElementById(
      `rpv-badge-veterinaria-${this._formId}`,
    );
    this._seccionesError = document.getElementById(
      `rpv-secciones-error-${this._formId}`,
    );

    this._cantidadInput?.addEventListener("input", this._onCantidadInput);
    this._switchRotacion.setOnChange(() => this._syncRotacionVisibility());
    this._switchPesaje.setOnChange(() => this._syncPesajeVisibility());
    this._switchVeterinaria.setOnChange(() => this._syncVeterinariaVisibility());
    // Delegado: un solo listener para todos los checkboxes de procedimientos.
    this._procedimientosContainer?.addEventListener(
      "change",
      this._onProcedimientoChange,
    );
    this._observacionesInput?.addEventListener(
      "input",
      this._onObservacionesInput,
    );
    if (this._observacionesInput) {
      this._observacionesInput.value = this._request.vetObservaciones ?? "";
    }

    // Estado inicial: origen ya conocido (fijo/edición) y visibilidad por flags.
    if (this._loteOrigenSelected) {
      this._prefillCantidad();
      this._syncPesoRows(this._maxCantidad());
    }
    this._syncRotacionVisibility();
    this._syncPesajeVisibility();
    this._syncVeterinariaVisibility();

    void this._loadProcedimientos();
  }

  dispose(): void {
    this._disposed = true;
    this._cantidadInput?.removeEventListener("input", this._onCantidadInput);
    this._pesoInputs.forEach((input) =>
      input.removeEventListener("input", this._onPesoInput),
    );
    this._procedimientosContainer?.removeEventListener(
      "change",
      this._onProcedimientoChange,
    );
    this._observacionesInput?.removeEventListener(
      "input",
      this._onObservacionesInput,
    );
    this._fields.forEach((field) => field.dispose());
    this._loteDestino.dispose();
    this._summaries.forEach((summary) => summary.dispose());
  }

  /** Valida los campos básicos y, si rotación está activa, destino + cantidad.
   *  Además fija `cantidadAnimales` y anula `loteDestinoId` si rotación quedó
   *  apagada (ids nunca `""`). */
  validate(): boolean {
    let isValid = true;
    this._fields.forEach((field) => {
      field.showError();
      if (field.getError()) {
        if (isValid) field.focus();
        isValid = false;
      }
    });

    // El evento debe registrar al menos una acción.
    const algunaActiva =
      this._request.isRotacion ||
      this._request.isPesaje ||
      this._request.isVeterinaria;
    if (!algunaActiva) {
      this._setSeccionesError(
        "Active al menos una acción: rotación, pesaje o veterinaria",
      );
      isValid = false;
    } else {
      this._setSeccionesError("");
    }

    if (this._request.isRotacion) {
      // Destino requerido.
      this._loteDestino.showError();
      if (this._loteDestino.getError()) isValid = false;

      // Cantidad ∈ [1, animales del origen].
      const max = this._maxCantidad();
      const cantidad = this._getCantidad();
      if (cantidad < 1 || cantidad > max) {
        this._setCantidadError(
          max > 0
            ? `Ingrese entre 1 y ${max}`
            : "Seleccione primero el potrero actual",
        );
        isValid = false;
      } else {
        this._setCantidadError("");
        this._request.cantidadAnimales = cantidad;
      }
    } else {
      // Rotación apagada: destino no viaja (null, nunca "") y la cantidad del
      // evento es la de pesos (si hay pesaje) o todos los animales del origen.
      this._request.loteDestinoId = null;
      this._request.cantidadAnimales = this._request.isPesaje
        ? this._pesoInputs.length
        : this._maxCantidad();
    }

    if (this._request.isPesaje) {
      // Todos los pesos son obligatorios (mínimo 0.1 kg).
      this._syncRequestPesos();
      const pesosOk =
        this._pesoInputs.length > 0 &&
        this._pesoInputs.every((input) => this._parse(input.value) >= 0.1);
      if (!pesosOk) {
        this._setPesosError("Ingrese el peso de cada animal (mínimo 0.1 kg)");
        isValid = false;
      } else {
        this._setPesosError("");
      }
    } else {
      // Pesaje apagado: los pesos no viajan.
      this._request.pesosAnimales = [];
      this._setPesosError("");
    }

    if (this._request.isVeterinaria) {
      // Al menos un procedimiento; observaciones opcionales (vacío → null).
      if (this._request.procedimientoIds.length === 0) {
        this._setProcedimientosError("Seleccione al menos un procedimiento");
        isValid = false;
      } else {
        this._setProcedimientosError("");
      }
      const observaciones = this._observacionesInput?.value.trim() ?? "";
      this._request.vetObservaciones =
        observaciones !== "" ? observaciones : null;
    } else {
      // Veterinaria apagada: procedimientos y observaciones no viajan.
      this._request.procedimientoIds = [];
      this._request.vetObservaciones = null;
      this._setProcedimientosError("");
    }

    return isValid;
  }

  private _rotacionBody: HTMLElement | null = null;
  private _cantidadInput: HTMLInputElement | null = null;
  private _cantidadError: HTMLElement | null = null;
  private _pesajeBody: HTMLElement | null = null;
  private _pesosContainer: HTMLElement | null = null;
  private _pesosError: HTMLElement | null = null;
  /** Inputs de peso individuales (uno por animal del potrero origen). */
  private _pesoInputs: HTMLInputElement[] = [];
  private _veterinariaBody: HTMLElement | null = null;
  private _procedimientosContainer: HTMLElement | null = null;
  private _procedimientosError: HTMLElement | null = null;
  private _observacionesInput: HTMLTextAreaElement | null = null;
  /** Badges "Se registrará:" del footer del modal (uno por sección). */
  private _badgeRotacion: HTMLElement | null = null;
  private _badgePesaje: HTMLElement | null = null;
  private _badgeVeterinaria: HTMLElement | null = null;
  private _seccionesError: HTMLElement | null = null;
  /** Si el form ya fue dispuesto (guarda de cargas async). */
  private _disposed = false;

  // ─── Card "Datos básicos" ───

  /** Selector del potrero actual (origen), filtrado a potreros con animales. */
  private _createLoteOrigen(): LoteSelectorField<RotacionPesajeVeterinariaRequest> {
    return new LoteSelectorField(
      this._request,
      {
        title: "Potrero origen",
        get: (r) => r.loteOrigenId,
        set: (r, v) => {
          r.loteOrigenId = v;
        },
        // Seleccionables: potreros con animales; al editar, también el origen
        // original (pudo quedar en 0 justamente por este evento).
        filter: (lote) =>
          lote.ganaderiaCantidadAnimales > 0 ||
          lote.id === this._loteOrigenOriginalId,
        onSelect: (lote) => this._onLoteOrigenSelected(lote),
      },
      { isRequired: true },
    );
  }

  /** Al elegir/resolver el potrero origen: lo guarda, precarga la cantidad a
   *  rotar y reconstruye los inputs de peso (uno por animal). */
  private _onLoteOrigenSelected(lote: ClienteFincaLoteResponse): void {
    this._loteOrigenSelected = lote;
    this._prefillCantidad();
    this._syncPesoRows(this._maxCantidad());
  }

  /** Fecha del evento (máximo hoy). */
  private _createFecha(): InputDateField<RotacionPesajeVeterinariaRequest> {
    return new InputDateField(this._request, {
      label: "Fecha",
      description: "Seleccione la fecha del evento",
      max: toISODate(new Date()),
      get: (r) => r.fecha,
      set: (r, v) => {
        r.fecha = v;
      },
    });
  }

  // ─── Card "Rotación de potrero" ───

  /** Switch de la sección de rotación (header de la card). */
  private _createSwitchRotacion(): InputSwitchField<RotacionPesajeVeterinariaRequest> {
    return new InputSwitchField(this._request, {
      label: "",
      get: (r) => r.isRotacion,
      set: (r, v) => {
        r.isRotacion = v;
      },
    });
  }

  /** Selector del potrero destino: cualquier potrero menos el origen actual. */
  private _createLoteDestino(): LoteSelectorField<RotacionPesajeVeterinariaRequest> {
    return new LoteSelectorField(
      this._request,
      {
        title: "Potrero destino",
        get: (r) => r.loteDestinoId ?? "",
        set: (r, v) => {
          r.loteDestinoId = v;
        },
        filter: (lote) => lote.id !== this._request.loteOrigenId,
      },
      { isRequired: true },
    );
  }

  /** Muestra/oculta el body de rotación (y su badge) según el switch. */
  private _syncRotacionVisibility(): void {
    if (!this._rotacionBody) return;
    this._rotacionBody.style.display = this._request.isRotacion ? "" : "none";
    if (!this._request.isRotacion) this._setCantidadError("");
    if (this._badgeRotacion) {
      this._badgeRotacion.style.display = this._request.isRotacion ? "" : "none";
    }
    this._setSeccionesError("");
  }

  /** Máximo de animales a rotar = animales del potrero origen; al editar el
   *  origen original, se suman los animales del evento original (el conteo
   *  actual ya los descontó si el evento rotó). */
  private _maxCantidad(): number {
    const base = this._loteOrigenSelected?.ganaderiaCantidadAnimales ?? 0;
    const esOrigenOriginal =
      this._loteOrigenSelected?.id === this._loteOrigenOriginalId;
    return this._rotacionOriginal && esOrigenOriginal
      ? base + this._cantidadOriginalEvento
      : base;
  }

  /** Precarga la cantidad: al editar, la del evento original; si no, todos los
   *  animales del origen (default y máx). */
  private _prefillCantidad(): void {
    if (!this._cantidadInput) return;
    const max = this._maxCantidad();
    const inicial = this._request.id
      ? Math.min(this._cantidadOriginalEvento, max)
      : max;
    this._cantidadInput.max = String(max);
    this._cantidadInput.value = inicial > 0 ? String(inicial) : "";
    this._request.cantidadAnimales = inicial;
  }

  /** Cantidad actual del input (clamp a [0, máx]). */
  private _getCantidad(): number {
    const raw = parseInt(this._cantidadInput?.value ?? "0", 10);
    if (isNaN(raw) || raw < 0) return 0;
    return Math.min(raw, this._maxCantidad());
  }

  /** Al cambiar la cantidad: clamp visual + persiste en el request. */
  private _onCantidadInput = (): void => {
    const cantidad = this._getCantidad();
    if (this._cantidadInput && String(cantidad) !== this._cantidadInput.value) {
      this._cantidadInput.value = cantidad > 0 ? String(cantidad) : "";
    }
    this._request.cantidadAnimales = cantidad;
    this._setCantidadError("");
  };

  private _setCantidadError(msg: string): void {
    if (this._cantidadError) this._cantidadError.textContent = msg;
  }

  // ─── Card "Pesaje" ───

  /** Switch de la sección de pesaje (header de la card). */
  private _createSwitchPesaje(): InputSwitchField<RotacionPesajeVeterinariaRequest> {
    return new InputSwitchField(this._request, {
      label: "",
      get: (r) => r.isPesaje,
      set: (r, v) => {
        r.isPesaje = v;
      },
    });
  }

  /** Muestra/oculta el body de pesaje (y su badge) según el switch. */
  private _syncPesajeVisibility(): void {
    if (!this._pesajeBody) return;
    this._pesajeBody.style.display = this._request.isPesaje ? "" : "none";
    if (!this._request.isPesaje) this._setPesosError("");
    if (this._badgePesaje) {
      this._badgePesaje.style.display = this._request.isPesaje ? "" : "none";
    }
    this._setSeccionesError("");
  }

  /** Ajusta el número de inputs de peso al `target` (uno por animal del origen),
   *  prellenando desde `request.pesosAnimales` (edición). */
  private _syncPesoRows(target: number): void {
    if (!this._pesosContainer) return;
    const current = this._pesoInputs.length;

    if (target > current) {
      for (let i = current; i < target; i++) {
        const cell = document.createElement("div");
        cell.className = "venta-peso-cell";

        const label = document.createElement("label");
        label.className = "venta-peso-label";
        label.textContent = String(i + 1);

        const input = document.createElement("input");
        input.className = "sx-input sx-input--numeric";
        input.type = "number";
        input.min = "0.1";
        input.step = "0.1";
        const prefill = this._request.pesosAnimales[i];
        if (prefill != null && prefill > 0) input.value = String(prefill);
        input.addEventListener("input", this._onPesoInput);

        cell.appendChild(label);
        cell.appendChild(input);
        this._pesosContainer.appendChild(cell);
        this._pesoInputs.push(input);
      }
    } else if (target < current) {
      for (let i = current - 1; i >= target; i--) {
        this._pesoInputs[i].removeEventListener("input", this._onPesoInput);
        this._pesoInputs[i].parentElement?.remove();
        this._pesoInputs.pop();
      }
    }

    this._updateSummaries();
  }

  /** Recalcula resúmenes y persiste los pesos al cambiar un input de peso. */
  private _onPesoInput = (): void => {
    this._syncRequestPesos();
    this._updateSummaries();
    this._setPesosError("");
  };

  /** Vuelca los pesos de los inputs al request (0 si vacío). */
  private _syncRequestPesos(): void {
    this._request.pesosAnimales = this._pesoInputs.map((input) =>
      this._parse(input.value),
    );
  }

  /** Recalcula peso mín/prom/máx y total (en vivo). */
  private _updateSummaries(): void {
    const values = this._pesoInputs
      .map((input) => this._parse(input.value))
      .filter((v) => v > 0);
    const total = values.reduce((s, v) => s + v, 0);
    const min = values.length > 0 ? Math.min(...values) : 0;
    const max = values.length > 0 ? Math.max(...values) : 0;
    const prom = values.length > 0 ? total / values.length : 0;

    this._pesoMin.setValue(min > 0 ? `${_2d(min)} kg` : null);
    this._pesoProm.setValue(prom > 0 ? `${_2d(prom)} kg` : null);
    this._pesoMax.setValue(max > 0 ? `${_2d(max)} kg` : null);
    this._pesoTotal.setValue(total > 0 ? `${_miles(total)} kg` : null);
  }

  /** Parsea un valor de input a número decimal (0 si inválido). */
  private _parse(raw: string): number {
    const n = parseFloat(raw);
    return isNaN(n) ? 0 : n;
  }

  private _setPesosError(msg: string): void {
    if (this._pesosError) this._pesosError.textContent = msg;
  }

  // ─── Card "Tratamiento veterinario" ───

  /** Switch de la sección de veterinaria (header de la card). */
  private _createSwitchVeterinaria(): InputSwitchField<RotacionPesajeVeterinariaRequest> {
    return new InputSwitchField(this._request, {
      label: "",
      get: (r) => r.isVeterinaria,
      set: (r, v) => {
        r.isVeterinaria = v;
      },
    });
  }

  /** Muestra/oculta el body de veterinaria (y su badge) según el switch. */
  private _syncVeterinariaVisibility(): void {
    if (!this._veterinariaBody) return;
    this._veterinariaBody.style.display = this._request.isVeterinaria
      ? ""
      : "none";
    if (!this._request.isVeterinaria) this._setProcedimientosError("");
    if (this._badgeVeterinaria) {
      this._badgeVeterinaria.style.display = this._request.isVeterinaria
        ? ""
        : "none";
    }
    this._setSeccionesError("");
  }

  /** Carga el catálogo y arma las cards seleccionables de procedimientos
   *  (checkbox + nombre + badge ICA + meta), marcando las ya elegidas (edición). */
  private async _loadProcedimientos(): Promise<void> {
    const procedimientos =
      await procedimientoVeterinarioGrpcCatalogService.getListAsync();
    if (this._disposed || !this._procedimientosContainer?.isConnected) return;

    this._procedimientosContainer.innerHTML = "";
    procedimientos.forEach((procedimiento) => {
      this._procedimientosContainer!.appendChild(
        this._createProcedimientoCard(procedimiento),
      );
    });
  }

  /** Card seleccionable de un procedimiento (label = click en toda la card). */
  private _createProcedimientoCard(
    procedimiento: ProcedimientoVeterinarioItem,
  ): HTMLLabelElement {
    const checked = this._request.procedimientoIds.includes(procedimiento.id);

    const card = document.createElement("label");
    card.className = `rpv-proc-card${checked ? " rpv-proc-card--active" : ""}`;

    const check = document.createElement("input");
    check.type = "checkbox";
    check.className = "rpv-proc-check";
    check.dataset.procId = procedimiento.id;
    check.checked = checked;

    const info = document.createElement("span");
    info.className = "rpv-proc-info";

    const nombre = document.createElement("span");
    nombre.className = "rpv-proc-nombre";
    nombre.textContent = procedimiento.nombre;
    if (procedimiento.isIca) {
      const ica = document.createElement("span");
      ica.className = "sx-badge sx-badge-error";
      ica.textContent = "ICA";
      nombre.appendChild(ica);
    }
    info.appendChild(nombre);

    if (procedimiento.meta) {
      const meta = document.createElement("span");
      meta.className = "rpv-proc-meta";
      meta.textContent = procedimiento.meta;
      info.appendChild(meta);
    }

    card.appendChild(check);
    card.appendChild(info);
    return card;
  }

  /** Delegado de los checkboxes: agrega/quita el procedimiento del request y
   *  resalta la card. */
  private _onProcedimientoChange = (e: Event): void => {
    const check = e.target as HTMLInputElement | null;
    const procId = check?.dataset.procId;
    if (!check || !procId) return;

    if (check.checked) {
      if (!this._request.procedimientoIds.includes(procId)) {
        this._request.procedimientoIds.push(procId);
      }
    } else {
      this._request.procedimientoIds = this._request.procedimientoIds.filter(
        (id) => id !== procId,
      );
    }
    check.closest(".rpv-proc-card")?.classList.toggle(
      "rpv-proc-card--active",
      check.checked,
    );
    this._setProcedimientosError("");
  };

  /** Persiste las observaciones en el request (normaliza a null en validate). */
  private _onObservacionesInput = (): void => {
    this._request.vetObservaciones = this._observacionesInput?.value ?? null;
  };

  private _setProcedimientosError(msg: string): void {
    if (this._procedimientosError) this._procedimientosError.textContent = msg;
  }

  private _setSeccionesError(msg: string): void {
    if (this._seccionesError) this._seccionesError.textContent = msg;
  }
}
